import { ID_COLUMN } from '../csv-import';
import { CsvImportConfig, ImportLookups, ImportOption, ImportValues } from '../csv-import-types';

/**
 * Nok email templates. Mirrors `NokEmailTemplateImporter` in HQ-API, which is the authority and
 * which also turns plain text into paragraphs for every type but the form messages.
 */

/** As the template edit form lists them. */
const TEMPLATE_TYPES: ImportOption[] = [
    { value: 'email_wrapper', label: 'Email Wrapper' },
    { value: 'ms_email_body', label: 'Mystery Shopper Message Body' },
    { value: 'ms_form_message', label: 'Mystery Shopper Form Message' },
    { value: 'cs_email_body', label: 'Compliance Sniffer Message Body' },
    { value: 'cs_form_message', label: 'Compliance Sniffer Form Message' },
];

const STATUSES: ImportOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'disabled', label: 'Disabled' },
];

/** The types whose emails go out with the template's subject. */
const needsSubject = (values: ImportValues): boolean =>
    ['ms_email_body', 'cs_email_body'].includes((values.type ?? '').toLowerCase());

/** The Mystery Shopper composer offers a body only for the sender's industry. */
const needsIndustry = (values: ImportValues): boolean => (values.type ?? '').toLowerCase() === 'ms_email_body';

const firstIndustries = (lookups: ImportLookups, count: number): string =>
    (lookups.industries ?? []).slice(0, count).map((option: ImportOption): string => option.value).join(';');

export const nokEmailTemplateImport: CsvImportConfig = {
    title: 'Nok Email Templates',
    endpoint: 'hq/nok-import/email-templates',
    fileName: 'nok-email-templates',
    columns: [
        ID_COLUMN,
        { key: 'name', label: 'Name', kind: 'text', required: true, maxLength: 100, width: 220 },
        { key: 'type', label: 'Type', kind: 'choice', required: true, choices: TEMPLATE_TYPES, width: 240 },
        {
            key: 'status', label: 'Status', kind: 'choice', choices: STATUSES, width: 120,
            helper: 'Empty is Active. A disabled template is never offered in a composer.',
        },
        {
            key: 'subject', label: 'Subject', kind: 'text', required: needsSubject, maxLength: 255, width: 220,
            helper: 'Required for the two message body types.',
        },
        {
            key: 'tags', label: 'Tags', kind: 'text', width: 180,
            helper: 'Separated by semicolons, such as sales;support. New tags are allowed.',
        },
        {
            key: 'industries', label: 'Industries', kind: 'references', lookup: 'industries', required: needsIndustry,
            acceptsNames: true, width: 260,
            helper: 'Industry names or ids, separated by commas or semicolons. Required for Mystery Shopper message '
                + 'bodies, which are offered only for these industries.',
        },
        {
            key: 'isBusiness', label: 'Is Business', kind: 'boolean', width: 120,
            helper: 'Yes for company senders, No for consumer senders, empty for either.',
        },
        {
            key: 'html', label: 'Html', kind: 'longText', required: true, width: 360,
            helper: 'Plain text with no markup becomes one paragraph per line, except for form messages, '
                + 'which stay plain text.',
        },
    ],
    lookups: [
        { key: 'industries', resource: 'industries', label: (record) => record.name + ' (' + record.id + ')' },
    ],
    sampleRows: (lookups: ImportLookups): ImportValues[] => [
        {
            name: 'Example Wrapper',
            type: 'email_wrapper',
            status: 'active',
            subject: '',
            tags: '',
            industries: '',
            isBusiness: '',
            html: '<html><head><title>{{nok_name}}</title></head>\n<body>\n{{email_content}}\n{{email_signature}}\n</body></html>',
        },
        {
            name: 'Example Mystery Shopper Body',
            type: 'ms_email_body',
            status: 'active',
            subject: 'Quick question about your services',
            tags: 'sales',
            industries: firstIndustries(lookups, 2),
            isBusiness: 'yes',
            html: 'Hi {{to_name}},\nWe are looking for a supplier and wondered if you could help.\nThanks',
        },
        {
            name: 'Example Compliance Form Message',
            type: 'cs_form_message',
            status: 'disabled',
            subject: '',
            tags: '',
            industries: '',
            isBusiness: '',
            html: 'Hello,\n\nPlease could you tell us how you handle personal data?\n\nThanks',
        },
    ],
};
